import React, { useState, useMemo, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import {
  parseSentenceWithBlanks,
  checkAllBlanksFilled
} from '../QuickPractice/fibHelpers';
import UmlautHelper from './UmlautHelper';
import UmlautButtons from './UmlautButtons';
import QuestionAudioPlayer from '../shared/QuestionAudioPlayer';

const OptionlessFillBlank = ({ question, onAnswer, onNext, sessionData = null, langCode = null }) => {
  const [userAnswers, setUserAnswers] = useState({});
  const inputRefs = useRef({});

  // Transform backend data to component format
  const transformedQuestion = useMemo(() => {
    const blanks = [];
    
    // Priority: use blank-passage if available
    const text = question['blank-passage'] || question.text || question['text-soal'] || '';
    
    // Parse [blank-1], [blank-2], etc. from blank-passage
    const blankMatches = text.match(/\[blank-\d+\]/g) || [];
    
    if (blankMatches.length > 0) {
      blankMatches.forEach((placeholder, index) => {
        // Extract blank number from [blank-1], [blank-2], etc.
        const blankNumberMatch = placeholder.match(/\[blank-(\d+)\]/);
        const blankNumber = blankNumberMatch ? parseInt(blankNumberMatch[1]) : index + 1;
        const blankId = blankNumber;
        
        // Get correct answer if available
        const answers = question.answers || question['correct-answers'] || [];
        const correctAnswer = Array.isArray(answers) && answers[index] !== undefined
          ? answers[index]
          : '';
        
        blanks.push({
          id: blankId,
          correctAnswer: correctAnswer,
        });
      });
    } else if (question.blanks && Array.isArray(question.blanks)) {
      // If blanks already structured
      question.blanks.forEach((blank, index) => {
        blanks.push({
          id: blank.id || blank['blank-id'] || index + 1,
          correctAnswer: blank['correct-answer'] || blank.correctAnswer || blank.answer || '',
        });
      });
    }

    return {
      ...question,
      blanks: blanks,
      text: text,
    };
  }, [question]);

  // Reset state when question changes
  useEffect(() => {
    setUserAnswers({});
  }, [question['soal-id'] || question.id]);

  const handleBlankChange = (blankId, value) => {
    setUserAnswers(prev => ({ ...prev, [blankId]: value }));
  };

  const handleSubmit = () => {
    if (!transformedQuestion.blanks || !checkAllBlanksFilled(transformedQuestion.blanks, userAnswers)) return;
    
    // Convert object answers to array format for backend
    // Sort by blank ID to ensure correct order
    const answersArray = transformedQuestion.blanks
      .sort((a, b) => a.id - b.id)
      .map(blank => userAnswers[blank.id] || '');
    
    if (onAnswer) {
      onAnswer({ 
        questionId: question['soal-id'] || question.id, 
        answer: answersArray, // Send as array instead of object
        isCorrect: false // Will be determined by backend
      });
    }
    if (onNext) onNext();
  };

  const fibInputCls =
    'inline-block min-w-[120px] max-md:min-w-[100px] py-2 px-3 max-md:py-2.5 max-md:px-3 border-2 border-[rgba(18,118,109,0.3)] dark:border-[rgba(20,184,166,0.3)] rounded-lg text-base max-md:text-[15px] font-semibold text-[var(--text-primary)] bg-[rgba(18,118,109,0.05)] dark:bg-[rgba(20,184,166,0.08)] transition-all duration-300 font-[inherit] focus:outline-none focus:border-[#12766d] focus:bg-[rgba(18,118,109,0.1)] dark:focus:border-[#14b8a6] dark:focus:bg-[rgba(20,184,166,0.12)] focus:shadow-[0_0_0_3px_rgba(18,118,109,0.1)] dark:focus:shadow-[0_0_0_3px_rgba(20,184,166,0.15)] placeholder:opacity-50 placeholder:font-medium placeholder:text-[rgba(26,26,46,0.4)] dark:placeholder:text-[var(--text-tertiary)] max-md:min-h-[44px]';

  const renderSentence = () => {
    if (!transformedQuestion.blanks || transformedQuestion.blanks.length === 0) {
      return (
        <p className="text-lg font-semibold text-[var(--text-primary)] leading-relaxed whitespace-pre-line">
          {transformedQuestion.text}
        </p>
      );
    }

    const parts = parseSentenceWithBlanks(transformedQuestion.text, transformedQuestion.blanks);
    if (!parts) return null;

    return (
      <div className="flex flex-wrap items-center gap-2 max-md:gap-1.5 text-lg max-md:text-base font-semibold text-[var(--text-primary)] leading-[1.8] mb-6 max-md:mb-5">
        {parts.map((part, index) => {
          if (part.type === 'text') {
            return <span key={index}>{part.content}</span>;
          }

          const blankId = part.blank.id || part.blankIndex;
          return (
            <input
              key={index}
              ref={(el) => {
                if (el) inputRefs.current[blankId] = el;
              }}
              type="text"
              className={fibInputCls}
              data-blank-id={blankId}
              value={userAnswers[blankId] || ''}
              onChange={(e) => handleBlankChange(blankId, e.target.value)}
              placeholder="..."
            />
          );
        })}
      </div>
    );
  };

  const allBlanksFilled = checkAllBlanksFilled(transformedQuestion.blanks, userAnswers);

  return (
    <motion.div
      className="flex flex-col gap-5"
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.25 }}
    >
      <div className="flex justify-between items-center pb-4 border-b-2 border-[rgba(18,118,109,0.1)] dark:border-[var(--border-color)]">
        <motion.span
          className="px-3.5 py-2 bg-gradient-to-br from-[#12766d] to-[#236463] text-white rounded-lg text-[11px] font-semibold uppercase tracking-wide whitespace-nowrap shrink-0 max-md:text-[10px] max-md:px-2 max-md:py-1"
          initial={{ scale: 0.85 }}
          animate={{ scale: 1 }}
          transition={{ type: 'spring', stiffness: 300 }}
        >
          Fill in the Blank
        </motion.span>
        <UmlautHelper langCode={langCode} />
      </div>
      
      <div className="flex flex-col gap-5">
        {(question?.['audio-url'] || question?.audio_url) && (
          <QuestionAudioPlayer audioUrl={question['audio-url'] || question.audio_url} />
        )}
        <UmlautButtons 
          langCode={langCode}
          onInsertChar={(char) => {
            // Get the currently focused/active input element
            const activeElement = document.activeElement;
            
            // Check if it's one of our FIB input fields
            if (activeElement &&
                activeElement.tagName === 'INPUT' &&
                activeElement.hasAttribute('data-blank-id')) {
              
              const start = activeElement.selectionStart || 0;
              const end = activeElement.selectionEnd || 0;
              
              // Get blankId from data attribute
              const blankIdAttr = activeElement.getAttribute('data-blank-id');
              const blankId = blankIdAttr ? parseInt(blankIdAttr, 10) : null;
              
              if (blankId !== null && !isNaN(blankId)) {
                const currentValue = userAnswers[blankId] || '';
                const newValue = currentValue.substring(0, start) + char + currentValue.substring(end);
                
                // Update state immediately
                setUserAnswers(prev => ({ ...prev, [blankId]: newValue }));
                
                // Restore focus and cursor position after state update
                setTimeout(() => {
                  // Use ref to get the input element
                  const input = inputRefs.current[blankId];
                  if (input) {
                    input.focus();
                    const newCursorPos = start + char.length;
                    if (input.setSelectionRange) {
                      input.setSelectionRange(newCursorPos, newCursorPos);
                    }
                  }
                }, 10);
              }
            }
          }}
        />
        {renderSentence()}
      </div>

      <div className="flex justify-center pt-4 border-t-2 border-[rgba(18,118,109,0.1)] dark:border-[var(--border-color)]">
        <motion.button
          type="button"
          className="py-3.5 px-7 rounded-xl border-none text-base font-semibold cursor-pointer transition-all duration-300 bg-gradient-to-br from-[#12766d] to-[#0f5d56] text-white shadow-[0_4px_12px_rgba(18,118,109,0.3)] hover:shadow-[0_6px_16px_rgba(18,118,109,0.4)] hover:-translate-y-0.5 disabled:opacity-60 disabled:cursor-not-allowed dark:bg-gradient-to-br dark:from-[#fbbf24] dark:to-[#f59e0b] dark:text-[#1d4746] dark:shadow-[0_4px_14px_rgba(251,191,36,0.35)] dark:hover:from-[#f59e0b] dark:hover:to-[#d97706] dark:hover:shadow-[0_6px_18px_rgba(251,191,36,0.45)] dark:disabled:from-[#334155] dark:disabled:to-[#1e293b] dark:disabled:text-[#64748b] dark:disabled:shadow-none dark:disabled:opacity-70 dark:disabled:hover:transform-none max-md:w-full max-md:py-3.5 max-md:px-6 max-md:min-h-[44px] max-md:text-[15px] touch-manipulation"
          onClick={handleSubmit}
          disabled={!allBlanksFilled}
          whileTap={{ scale: 0.96 }}
        >
          Submit
        </motion.button>
      </div>
    </motion.div>
  );
};

export default OptionlessFillBlank;

